import Alpine from "alpinejs";

const endpointUrl = "https://api.submarineplatform.com/graphql";
const tokenUrl = "/apps/platform/tokens";
const formatter = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit",
  month: "short",
  year: "numeric",
});


async function graphqlFetch(query: string, variables: Record<string, any>) {
  const response = await fetch(endpointUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization":`Bearer ${await fetchToken()}`
    },
    body: JSON.stringify({ query, variables }),
  });
  return await response.json();
}

async function storefrontFetch(query: string, variables: Record<string, any> = {}) {
  const response = await fetch(window.Shopify.routes.root + 'api/2023-10/graphql.json', {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": await getStorefrontAccessToken(),
    },
    body: JSON.stringify({ query, variables }),
  });
  return await response.json();
}

async function getStorefrontAccessToken(): Promise<string> {
  if (window.Shopify?.storefrontAccessToken) {
    return window.Shopify.storefrontAccessToken;
  }
  
  throw new Error('Shopify Storefront Access Token not found');
}

function daysUntilDate(dateString: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const targetDate = new Date(dateString);
  targetDate.setHours(0, 0, 0, 0);

  const diffTime = targetDate.getTime() - today.getTime();
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

  return diffDays;
}

async function fetchToken() {
  try {
    const response = await fetch(tokenUrl, {
      method: "GET",
      mode: "cors",
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    return await response.text();
  } catch (error) {
    
  }
}

export function initSubmarineComponent() {
  Alpine.data("submarineManageSubscription", (subscriptionId: String) => {
    return {
      feedbackModal: {} as { title: string; content: string; link: string; open: boolean },
      getDate(myDate: Date) {
        return formatter.format(new Date(myDate));
      },
      paymentMethodUpdateEmailSent: false,
      loading: true,
      updateAddressLoading: false,
      openAddressUpdateModal: false,
      toggleOpenAddressUpdateModal() {
        this.openAddressUpdateModal = !this.openAddressUpdateModal;
      },
      subscription: {} as {
        order: {
          name: string;
        };
        lines: [
          {
            productVariant: {
              title: string;
            };
          },
        ];
        lastProcessedOrder: Date;
        nextBillingAt: Date;
        paymentMethod: {
          activePaymentInstrument: {
            paymentSource: {
              last4: string;
            };
          };
        };
        deliveryMethod: {
          address: {
            city: string;
            country: {
              name: string;
            };
            firstName: string;
            lastName: string;
            phone: string;
            postcode: string;
            province: {
              code: string;
            };
            street1: string;
            street2: string;
          };
        };
        nextScheduledOrder: {
          paymentIntent: {
            amount: {
              amount: string;
              currency: string;
            };
          };
        };
      },
      email: "",
      async processSubscriptionOrder() {
        this.loading = true;
        const response = await graphqlFetch(PROCESS_SUBSCRIPTION_ORDER, {
          input: { id: this.subscription.nextScheduledOrder.id },
        });
        try {
          const { data } = response;
          this.subscription = data.subscriptionOrderProcess.subscriptionOrder.subscription;
          this.feedbackModal = {
            title: "Order Processed",
            content: `<div>Your order has been processed and will be pick, packed & shipped as soon as possible</div><div>You will find the order in your <a href="/account" class="underline">Order History</a> page</div>`,
            link: "/account",
            linkText: "Order History",
            open: true,
          };
        } catch {
          this.feedbackModal = {
            title: "Order Processing Failed",
            content: `<div>There was an error processing this order. <br/> Please contact support</div>`,
            open: true,
          };
        }

        this.loading = false;
      },
      async setSchedule(event: Event) {
        this.loading = true;
        const form = event.target as HTMLFormElement;
        const formData = new FormData(form);
        const response = await graphqlFetch(SET_SUBSCRIPTION_SCHEDULE, {
          input: {
            id: this.subscription.id,
            nextDeliveryAt: formData.get("date"),
            subscriptionAnchorId: this.subscription.subscriptionAnchor.id,
            subscriptionPlanId: this.subscription.subscriptionPlan.id,
          },
        });
        try {
          const { data } = response;
          this.subscription = data.subscriptionSetSchedule.subscription;
          this.feedbackModal = {
            title: "Order Rescheduled",
            content: `<div>Your order has been rescheduled to ${formData.get("date")}</div>`,
            open: true,
          };
        } catch {
          this.feedbackModal = {
            title: "Order Rescheduling Failed",
            content: `<div>There was an error rescheduling this order. <br/> Please contact support</div>`,
            open: true,
          };
        }
        this.loading = false;
      },
      async changeFrequency(event: Event) {
        this.loading = true;
        const form = event.target as HTMLFormElement;
        const formData = new FormData(form);
        const { selectedPlan } = JSON.parse(formData.get("deliveryFrequency"));
        if (this.subscription.subscriptionAnchor.id != selectedPlan.anchorId) {
          const response = await graphqlFetch(SET_SUBSCRIPTION_SCHEDULE, {
            input: {
              id: this.subscription.id,
              nextDeliveryAt: this.subscription.nextScheduledOrder.expectedDeliveryAt,
              subscriptionAnchorId: selectedPlan.anchorId,
              subscriptionPlanId: selectedPlan.planId,
            },
          });
          try {
          const { data } = response;
          this.subscription = data.subscriptionSetSchedule.subscription;
          
       
          const flavorData = await this.fetchSubscriptionFlavors();
          if (this.subscription.lines && this.subscription.lines.length > 0 && flavorData) {
            const lineItem = this.subscription.lines[0];
            this.subscription.lines = [{
              ...lineItem,
              currentFlavors: flavorData.currentFlavors || [],
              flavorsOptions: flavorData.flavors || []
            }];
          }
          
          this.feedbackModal = {
            title: "Order Frequency Updated",
            content: `<div>Your delivery frequency has been changed to ${selectedPlan.description}.`,
            open: true,
          };
          }
          catch {
            this.feedbackModal = {
              title: "Delivery Frequency Update Failed",
              content: `<div>There was an error editing the delivery frequency with this subscription. <br/> Please contact support</div>`,
              open: true,
            };
          }
        }
        this.loading = false;
      },
      async skipSubscriptionOrder() {
        this.loading = true;
        const response = await graphqlFetch(SKIP_SUBSCRIPTION_ORDER, {
          input: { id: this.subscription.nextScheduledOrder.id },
        });
        try {
          const { data } = response;
          this.subscription = data.subscriptionOrderSkip.subscriptionOrder.subscription;
          this.feedbackModal = {
            title: "Order Skipped",
            content: `<div>Your next order has been skipped</div>`,
            open: true,
          };
        } catch {
          this.feedbackModal = {
            title: "Order Skipping Failed",
            content: `<div>Your order has not been skipped <br/> Please contact support</div>`,
            open: true,
          };
        }
        this.loading = false;
      },
      async sendPaymentMethodUpdateEmail() {
        const response = await graphqlFetch(SEND_PAYMENT_METHOD_UPDATE_EMAIL, {
          input: { id: this.subscription.paymentMethod.id },
        });
        const { data } = response;
        if (data.paymentMethodSendUpdateEmail.userErrors.length > 0) {
          
        } else {
          this.paymentMethodUpdateEmailSent = true;
        }
      },
      async allVariantAddOne(): Promise<void> {
        interface LineItem {
          productVariantId: string;
          quantity: number;
        }

        if (!this.subscription?.lines || this.subscription.lines.length === 0) return;

        const lineItem = this.subscription.lines[0];
        const lines: LineItem[] = [{
          productVariantId: `gid://external/ProductVariant/${lineItem.productVariant.externalId}`,
          quantity: lineItem.quantity + 1,
        }];

        interface SubscriptionUpdateResponse {
          data: {
            subscriptionUpdate: {
              subscription: typeof this.subscription;
            };
          };
        }

        const response: SubscriptionUpdateResponse = await graphqlFetch(SUBSCRIPTION_UPDATE_MUTATION_OBJECT, {
          input: {
            id: this.subscription.id,
            lines,
          },
        });

        if (response?.data?.subscriptionUpdate?.subscription) {
          this.subscription = response.data.subscriptionUpdate.subscription;
          
          await this.syncSubscriptionFlavors();
        }
      },
      async swapFlavour(targetId: string, replacementId: string): Promise<void> {
        this.loading = true;
        interface LineItem {
          productVariantId: string;
          quantity: number;
        }

        if (!this.subscription?.lines || this.subscription.lines.length === 0) return;

        const lineItem = this.subscription.lines[0];
        const lines: LineItem[] = [{
          productVariantId: lineItem.productVariant.externalId === targetId
            ? `gid://external/ProductVariant/${replacementId}`
            : `gid://external/ProductVariant/${lineItem.productVariant.externalId}`,
          quantity: lineItem.quantity,
        }];

        interface SubscriptionUpdateResponse {
          data: {
            subscriptionUpdate: {
              subscription: typeof this.subscription;
            };
          };
        }

        const response: SubscriptionUpdateResponse = await graphqlFetch(SUBSCRIPTION_UPDATE_MUTATION_OBJECT, {
          input: {
            id: this.subscription.id,
            lines,
          },
        });

        if (response?.data?.subscriptionUpdate?.subscription) {
          this.subscription = response.data.subscriptionUpdate.subscription;
          
          await this.syncSubscriptionFlavors();
        }
        this.loading = false;
      },
      async syncSubscriptionFlavors(customFlavorData?: any[]): Promise<void> {
        try {
          let flavorsToSync;
          
          if (customFlavorData) {
            flavorsToSync = customFlavorData;
          } else {
            const flavorData = await this.fetchSubscriptionFlavors();
            flavorsToSync = flavorData?.currentFlavors || [];
          }
          
          if (flavorsToSync && flavorsToSync.length > 0) {
            const syncPayload = {
              subscriptionId: this.subscription.id,
              flavors: flavorsToSync.map((flavor: any) => ({
                id: flavor.id,
                quantity: flavor.quantity,
                title: flavor.title
              }))
            };

            const response = await fetch('/apps/arcbridge/shopify/proxy/submarine/sync', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                "ngrok-skip-browser-warning": "true"
              },
              body: JSON.stringify(syncPayload)
            });

            if (!response.ok) {
              throw new Error(`Sync API error! Status: ${response.status}`);
            }

            const result = await response.json();
          }
        } catch (error) {
          
        }
      },
      async fetchSubscriptionFlavors(): Promise<any> {
        try {

          const subscriptionId = this.subscription.id.split('/').pop();
          

          const response = await fetch(`/apps/arcbridge/shopify/proxy/submarine/flavours/${subscriptionId}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              "ngrok-skip-browser-warning": "true"
            }
          });

          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }

          const apiData = await response.json();


          const flavorOptions = await this.getFlavorOptionsFromMetafields();
      
          if (apiData.success && apiData.results && apiData.results.flavours) {

            const currentFlavors = apiData.results.flavours.map((flavor: any) => {

              const flavorOption = flavorOptions?.find((option: any) => option.id === flavor.id);
              return {
                id: flavor.id,
                quantity: flavor.quantity,
                title: flavor.title,
                imageUrl: flavorOption?.imageUrl
              };
            });

            return {
              subscriptionId: this.subscription.id,
              currentFlavors: currentFlavors,
              flavors: flavorOptions || []
            };
          } else {
            console.warn('No flavors found for subscription:', apiData);
            return {
              subscriptionId: this.subscription.id,
              currentFlavors: [],
              flavors: flavorOptions || []
            };
          }
        } catch (error) {
          console.error('Error fetching subscription flavors:', error);

          const flavorOptions = await this.getFlavorOptionsFromMetafields();
          return {
            subscriptionId: this.subscription.id,
            currentFlavors: [],
            flavors: flavorOptions || []
          };
        }
      },
      async getFlavorOptionsForSwapping(currentFlavorId: string): Promise<any[]> {
    
        const allOptions = await this.getFlavorOptionsFromMetafields();
        

        const currentFlavor = allOptions.find(option => option.id === currentFlavorId);
        if (!currentFlavor) {
          return [];
        }
        
  
        return allOptions.filter(option => option.productId === currentFlavor.productId);
      },
      async getFlavorOptionsFromMetafields(filterByVariantId?: string): Promise<any[]> {
        try {
          if (!this.subscription?.lines || this.subscription.lines.length === 0) {
            return [];
          }


          const lineItem = this.subscription.lines[0];
          const variantId = lineItem.productVariant.externalId;
          
          if (!variantId) {
            return [];
          }


          const variantQuery = `
            query GetProductIdFromVariantId($id: ID!) {
              node(id: $id) {
                ... on ProductVariant {
                  product {
                    id
                  }
                }
              }
            }
          `;

          const variantResponse = await storefrontFetch(variantQuery, { 
            id: `gid://shopify/ProductVariant/${variantId}` 
          });

          if (variantResponse.errors) {
            throw new Error('Failed to fetch variant data from Storefront API');
          }

          const productId = variantResponse.data?.node?.product?.id;
          if (!productId) {
            throw new Error('Product ID not found from variant');
          }
          const query = `
            query GetProductWithBundlingById($id: ID!) {
              product(id: $id) {
                id
                handle
                title
                metafield(namespace: "bundling", key: "step_1_products") {
                  value
                  reference {
                    ... on Product {
                      id
                      variants(first: 50) {
                        edges {
                          node {
                            id
                            title
                            availableForSale
                            image {
                              url
                            }
                          }
                        }
                      }
                    }
                  }
                  references(first: 10) {
                    edges {
                      node {
                        ... on Product {
                          id
                          variants(first: 50) {
                            edges {
                              node {
                                id
                                title
                                availableForSale
                                image {
                                  url
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
                step2: metafield(namespace: "bundling", key: "step_2_products") {
                  value
                  references(first: 10) {
                    edges {
                      node {
                        ... on Product {
                          id
                          variants(first: 50) {
                            edges {
                              node {
                                id
                                title
                                availableForSale
                                image {
                                  url
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
                step3: metafield(namespace: "bundling", key: "step_3_products") {
                  value
                  references(first: 10) {
                    edges {
                      node {
                        ... on Product {
                          id
                          variants(first: 50) {
                            edges {
                              node {
                                id
                                title
                                availableForSale
                                image {
                                  url
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
                step4: metafield(namespace: "bundling", key: "step_4_products") {
                  value
                  references(first: 10) {
                    edges {
                      node {
                        ... on Product {
                          id
                          variants(first: 50) {
                            edges {
                              node {
                                id
                                title
                                availableForSale
                                image {
                                  url
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
                step5: metafield(namespace: "bundling", key: "step_5_products") {
                  value
                  references(first: 10) {
                    edges {
                      node {
                        ... on Product {
                          id
                          variants(first: 50) {
                            edges {
                              node {
                                id
                                title
                                availableForSale
                                image {
                                  url
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          `;

          const response = await storefrontFetch(query, { id: productId });
          
          if (response.errors) {
            console.error('Storefront API errors:', response.errors);
            throw new Error('Failed to fetch product data from Storefront API');
          }

          const product = response.data?.product;
          if (!product) {
            throw new Error('Product not found');
          }

          let allVariants: any[] = [];
          
          const steps = ['metafield', 'step2', 'step3', 'step4', 'step5'];
          
          steps.forEach(stepKey => {
            const stepData = product[stepKey];
            if (stepData?.references?.edges) {
              stepData.references.edges.forEach((productEdge: any) => {
                const bundleProduct = productEdge.node;
                if (bundleProduct?.variants?.edges) {
                  bundleProduct.variants.edges.forEach((variantEdge: any) => {
                    const variant = variantEdge.node;
                    if (variant.availableForSale) {
                      allVariants.push({
                        id: variant.id.replace('gid://shopify/ProductVariant/', ''),
                        title: variant.title,
                        imageUrl: variant.image?.url,
                        productId: bundleProduct.id 
                      });
                    }
                  });
                }
              });
            }
          });


         
          if (filterByVariantId) {
         
            const targetVariant = allVariants.find(v => v.id === filterByVariantId);
            if (targetVariant) {
              allVariants = allVariants.filter(v => v.productId === targetVariant.productId);
            }
          }

          return allVariants;
        } catch (error) {
          console.error('Error fetching flavor options from Storefront API:', error);

          return [];
        }
      },
      async updateAddress(event: Event) {
        this.updateAddressLoading = true;
        const form = event.target as HTMLFormElement;
        const formData = new FormData(form);

        const { data } = await graphqlFetch(SUBSCRIPTION_UPDATE_MUTATION_OBJECT, {
          input: {
            id: this.subscription.id,
            deliveryMethod: {
              shipping: {
                address: {
                  city: formData.get("city"),
                  country: {
                    code: formData.get("country"),
                  },
                  firstName: formData.get("firstName"),
                  lastName: formData.get("lastName"),
                  postcode: formData.get("postcode"),
                  province: {
                    code: formData.get("province"),
                  },
                  street1: formData.get("street1"),
                  street2: formData.get("street2"),
                  phone: formData.get("phone"),
                },
              },
            },
          },
        });

        this.subscription = data.subscriptionUpdate.subscription;
        this.openAddressUpdateModal = false;
        this.updateAddressLoading = false;
      },
      async cancelSubscription(event: Event) {
        this.loading = true;
        const { data } = await graphqlFetch(CANCEL_SUBSCRIPTION, {
          input: {
            id: this.subscription.id,
          },
        });

        this.subscription = data.subscriptionCancel.subscription;
        this.loading = false
        this.feedbackModal = {
          title: "Subscription Cancelled",
          content: `<div>The subscription has been cancelled. <br/> Please contact support if this was a mistake.</div>`,
          open: true,
        };
      },
      async init() {
        try {
          const response = await graphqlFetch(SUBSCRIPTION_QUERY, { subscriptionId });
          if (response?.data?.subscription) {
            this.subscription = response.data.subscription;
            this.address = response.data.subscription.deliveryMethod?.address || {};
            
            const flavorData = await this.fetchSubscriptionFlavors();
            
            if (this.subscription.lines && this.subscription.lines.length > 0 && flavorData) {
              const lineItem = this.subscription.lines[0];
              this.subscription.lines = [{
                ...lineItem,
                currentFlavors: flavorData.currentFlavors || [],
                flavorsOptions: flavorData.flavors || []
              }];
            }
          }
        } catch (error) {
          
        } 
        
        this.loading = false;
        this.updateAddressLoading = false;
      },
    };
  });

  Alpine.data("submarineGetSubscriptions", (customerId: String) => {
    return {
      loading: true,
      subscriptions: [] as {
        id: string;
        order: { name: string };
        nextScheduledOrder: {
          expectedDeliveryAt: Date;
          status: string;
          paymentIntent: {
            amount: {
              amount: string;
              currency: string;
            };
          };
        };
        daysToNextScheduledOrder: number;
        subscriptionAnchor: {
          frequency: string;
        };
      }[],
      daysUntilDate(dateString: string): number {
        formatter.format(new Date(dateString))
        const today = new Date();
        today.setHours(0, 0, 0, 0);
      
        const targetDate = new Date(dateString);
        targetDate.setHours(0, 0, 0, 0);
      
        const diffTime = targetDate.getTime() - today.getTime();
        const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
      
        return diffDays;
      },
      async init() {
        try {
          const response = await graphqlFetch(
            `
              #graphql
              query($customerIds: [SharedGlobalID!]){
                  searchSubscriptions(customerIds: $customerIds) {
                      subscriptions {
                          nodes {
                              id
                              order {
                                  name
                              }
                              nextScheduledOrder {
                                  expectedDeliveryAt
                                  status
                                  paymentIntent {
                                      amount {
                                          amount
                                          currency
                                      }
                                  }
                              }
                              subscriptionAnchor {
                                  frequency: name
                              }
                              status
                          }
                      }
                  }
              }`,
            { customerIds: [customerId] },
          );
          
          if (response?.data?.searchSubscriptions?.subscriptions?.nodes) {
            this.subscriptions = response.data.searchSubscriptions.subscriptions.nodes;
          } else {
            this.subscriptions = [];
          }
        } catch (error) {
          this.subscriptions = [];
        }
        
        this.loading = false;
      },
    };
  });

  Alpine.data("submarine", () => {
    return {
      loading: true,
      subscriptions: [] as { id: string; name: string; status: string }[],
      async init() {
        const { data } = await graphqlFetch(
          /* GraphQL */ `
            query {
              subscriptionPlans {
                nodes {
                  id
                  name
                  status
                }
              }
            }
          `,
          {},
        );

        this.subscriptions = data.subscriptionPlans.nodes;
        this.loading = false;
      },
    };
  });
}

/**
 <div x-data="submarine()" class="my-12 bg-blue-100 p-4">
 <p>Submarine:</p>

 <template x-if="loading">
 <p>Loading...</p>
 </template>

 <ul>
 <template x-for="subscription in subscriptions">
 <li>
 <p x-text="subscription.id"></p>
 <p x-text="subscription.name"></p>
 <p x-text="subscription.status"></p>
 </li>
 </template>
 </ul>
 </div>
 */

export const SUBSCRIPTION_FRAGMENT = `#graphql
 fragment SubscriptionDetails on Subscription {
 status
  availableSubscriptionPlans {
      id
      status
      anchors {
        id
        position
      }
      frequency {
        description
      }
    }
  subscriptionPlan {
    id
  }
  subscriptionAnchor {
    id
  }
  id
  processedSubscriptionOrdersCount
  order {
    name
  }
  lines {
    quantity
    productVariant {
    externalId
      title
      imageUrl
    }
    product {
        productVariants {
          imageUrl
          externalId
          title
        }
        title
      }
  }
  lastProcessedOrder {
    processedAt
    expectedBillingAt
  }
  nextBillingAt
  paymentMethod {
  id
    activePaymentInstrument {
      paymentSource {
        ... on Card {
          last4
          brand
        }
      }
    }
  }
  deliveryMethod {
    ... on SubscriptionDeliveryMethodShipping {
      address {
        city
        country {
          name
          code
        }
        firstName
        lastName
        phone
        postcode
        province {
          code
        }
        street1
        street2
      }
    }
  }
  nextScheduledOrder {

    nextSubscriptionOrder {
        expectedDeliveryAt
      }
    id
    lastPaymentMethodUpdateEvent {
      action
      createdAt
      description
    }
    expectedDeliveryAt
    paymentIntent {
      amount {
        amount
        currency
      }
    }
    financials {
      totalPrice {
        amount
      }
      subtotal {
        amount
      }
      discounts {
        total {
          amount
        }
      }
    }
  }
}`;

const SUBSCRIPTION_QUERY = `#graphql
${SUBSCRIPTION_FRAGMENT}
query SearchSubscriptions($subscriptionId: GlobalID!) {
  subscription(id: $subscriptionId) {
    ...SubscriptionDetails
  }
}`;

const SUBSCRIPTION_UPDATE_MUTATION_OBJECT = `#graphql
${SUBSCRIPTION_FRAGMENT}
mutation SubscriptionMutation($input: SubscriptionUpdateInput!) {
  subscriptionUpdate(input: $input) {
    subscription {
      ...SubscriptionDetails
    }
    userErrors {
      field
      message
    }
  }
}`;

const CANCEL_SUBSCRIPTION = `#graphql
${SUBSCRIPTION_FRAGMENT}
  mutation subscriptionCancel($input: SubscriptionCancelInput!) {
    subscriptionCancel(input: $input) {
      subscription {
        ...SubscriptionDetails
      }
      userErrors {
        field
        message
      }
    }
  }
`;

const PROCESS_SUBSCRIPTION_ORDER = `#graphql
${SUBSCRIPTION_FRAGMENT}
  mutation SubscriptionOrderProcess($input: SubscriptionOrderProcessInput!) {
    subscriptionOrderProcess(input: $input) {
      subscriptionOrder {
        id
        paymentStatus
        status
        subscription {
          ...SubscriptionDetails
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;
export const SKIP_SUBSCRIPTION_ORDER = `#graphql
 ${SUBSCRIPTION_FRAGMENT}
  mutation subscriptionOrderSkip($input: SubscriptionOrderSkipInput!) {
    subscriptionOrderSkip(input: $input) {
      subscriptionOrder {
        id
        status
        subscription {
          ...SubscriptionDetails
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

export const PAYMENT_METHOD_DETAILS = `
  activePaymentInstrument {
    description
    paymentSource {
      ... on Card {
        brand
        expiry {
          month
          year
        }
        last4
      }
    }
  }
  externalId
  id
  status
`;

export const GET_PAYMENT_METHOD = `#graphql
  query paymentMethod($id: GlobalID!) {
    paymentMethod(id: $id) {
      ${PAYMENT_METHOD_DETAILS}
    }
  }
`;

export const SEND_PAYMENT_METHOD_UPDATE_EMAIL = `#graphql
  mutation paymentMethodSendUpdateEmail($input: PaymentMethodSendUpdateEmailInput!) {
    paymentMethodSendUpdateEmail(input: $input) {
      userErrors {
        field
        message
      }
    }
  }
`;

export const SET_SUBSCRIPTION_SCHEDULE = `#graphql
 ${SUBSCRIPTION_FRAGMENT}
 mutation SubscriptionSetSchedule($input: SubscriptionSetScheduleInput!) {
  subscriptionSetSchedule(input: $input) {
    subscription {
      ...SubscriptionDetails
    }
    upcomingDeliverySlots {
      deliverAt
    }
    userErrors {
      field
      message
    }
  }
}
`;
